import { FC, useCallback, useMemo } from 'react';

import IngestionButton from './IngestionButton';
import { IDataSourceItem } from 'Modules/Studio/DataSources/redux/initialState';
import { IActiveIngestion } from 'Modules/Studio/components/StudioInfo/redux/ingestion.interface';
import { useIngestionByDataSourceContext } from '../../../context/DataSourceIngestion.context';
import { getDSViewIsDisabled, getDSViewIsRunningIngestion, getDSViewToolTipText } from './helpers';

type TProps = Pick<IDataSourceItem, 'id'> & {
  activeIngestion: IActiveIngestion | null;
};

/**
 * Use case: Ingestion button at data source view level
 * @param param0
 * @returns FC
 */
const DSViewIngestionByDataSourceButton: FC<TProps> = ({ id, activeIngestion }) => {
  const {
    runIngestionByDS,
    stopIngestion: stopIngestionById,
    isFullIngestionActive,
  } = useIngestionByDataSourceContext();

  const isRunningIngestion: boolean = useMemo(
    () => getDSViewIsRunningIngestion(activeIngestion),
    [activeIngestion]
  );

  const isDisabled = useMemo(() => getDSViewIsDisabled(activeIngestion), [activeIngestion]);

  const toolTipText = useMemo(() => {
    return getDSViewToolTipText(isDisabled, isRunningIngestion, isFullIngestionActive);
  }, [isDisabled, isRunningIngestion, isFullIngestionActive]);

  /**
   * Start ingestion at data source level
   */
  const startIngestion = useCallback(() => {
    runIngestionByDS(id);
  }, [runIngestionByDS, id]);

  /**
   * Stop ingestion at data source level OR stop ingestion at table level(s)
   */
  const stopIngestion = useCallback(() => {
    const activeTables = Object.keys(activeIngestion?.tables || {});
    if (activeTables.length === 0) {
      // ingestion at datasource level
      stopIngestionById(activeIngestion?.ingestionId || '');
    } else {
      // ingestion is running from multiple tables
      Object.keys(activeIngestion?.tables || {}).forEach((tableName) => {
        stopIngestionById(activeIngestion?.tables?.[tableName]?.ingestionId || '');
      });
    }
  }, [activeIngestion, stopIngestionById]);

  return (
    <IngestionButton
      id={id}
      isRunningIngestion={isRunningIngestion}
      isDisabled={isDisabled}
      toolTipText={toolTipText}
      startIngestion={startIngestion}
      stopIngestion={stopIngestion}
    />
  );
};

export default DSViewIngestionByDataSourceButton;
